import { productImages } from './siteConfig';
import type { DetailRow, Faq, Feature, ProductImage, StoryStep } from '../types/content';

export const features: Feature[] = [
  {
    title: 'Laser Facial Technology',
    body: 'Professionally designed for home and medical office use.',
  },
  {
    title: 'Handheld Controller',
    body: 'The primary control, with a clear display and simple buttons.',
  },
  {
    title: 'Optional “Hi Mask” Voice Control',
    body: 'Activate voice mode on the mask, then say “Hi Mask” to give commands.',
  },
];

export const galleryImages: ProductImage[] = [
  productImages.white,
  productImages.voice,
  productImages.emitters,
];

export const storySteps: StoryStep[] = [
  {
    id: 'fit',
    title: 'Put It On',
    body: 'The soft, flexible mask rests over the face and is held in place by a head strap.',
    image: productImages.white,
  },
  {
    id: 'voice',
    title: 'Activate Voice Mode',
    body: 'Short-press the white side button, then say “Hi Mask.” Voice control is a secondary option; the handheld controller is the primary control.',
    image: productImages.voice,
  },
  {
    id: 'controller',
    title: 'Use The Controller',
    body: 'Use the handheld controller to select a mode, adjust brightness and set the timer.',
    image: productImages.controller,
  },
  {
    id: 'manual',
    title: 'Choose Your Mode',
    body: 'Choose red plus near-infrared or blue plus near-infrared. The controller offers three brightness levels and 5, 10 or 15-minute timer settings.',
    image: productImages.emitters,
  },
];

export const kitContents: string[] = [
  'AuraGlow Luxe mask in White',
  'Handheld controller',
  'USB-C charging cable',
  'User manual',
];

export const productDetails: DetailRow[] = [
  { label: 'Color', value: 'White' },
  { label: 'Model', value: 'CS-002' },
  { label: 'Technology', value: 'Laser + Blue LED' },
  { label: 'Laser emitters', value: '164' },
  { label: 'Blue LEDs', value: '72' },
  { label: 'Wavelengths', value: '460, 665, 850 and 1064 nm' },
  { label: 'Operating modes', value: 'M1: 665 + 850 + 1064 nm · M2: 460 + 850 nm' },
  { label: 'Brightness', value: '3 levels' },
  { label: 'Timer', value: '5, 10 or 15 minutes' },
  { label: 'Shell', value: 'Silicone' },
  { label: 'Eye area', value: 'Wide, open eye opening' },
  { label: 'Fit', value: 'Head strap' },
  { label: 'Controls', value: 'Handheld controller with optional “Hi Mask” voice control' },
  { label: 'Charging', value: 'USB-C cable included · 3-hour charge, per manual' },
  { label: 'Battery capacity', value: '1,800 mAh' },
  { label: 'Guidance', value: 'User manual included' },
];

export const faqs: Faq[] = [
  {
    id: 'technology',
    question: 'What Type Of Light Technology Does The Mask Use?',
    answer: 'The CS-002 manual lists 164 laser emitters and 72 blue LEDs across four wavelengths: 460, 665, 850 and 1064 nm. These run in two combined operating modes, rather than four separate single-wavelength modes.',
  },
  {
    id: 'light-colors',
    question: 'What Do The Different Light Colors Mean?',
    answer: 'The mask combines visible blue light at 460 nm and red light at 665 nm with invisible near-infrared light at 850 and 1064 nm. The colored illustrations explain individual wavelengths; the mask uses them in two combined operating modes.',
  },
  {
    id: 'modes',
    question: 'What Are The Two Operating Modes?',
    answer: 'M1 combines red light at 665 nm with near-infrared light at 850 and 1064 nm. M2 combines blue light at 460 nm with near-infrared light at 850 nm. Use the controller’s M button to change modes. Three brightness levels and 5, 10 or 15-minute timer settings are available.',
  },
  {
    id: 'wrinkles',
    question: 'How Does Light Therapy Relate To Fine Lines And Wrinkles?',
    answer: 'Red and near-infrared light are studied in skincare for signs of aging, including the appearance of fine lines and wrinkles. Research on light therapy does not establish the same result for every device or person. Follow the product manual and discuss suitability with your dermatologist.',
  },
  {
    id: 'colors',
    question: 'Which Colors Are Available?',
    answer: 'The AuraGlow Luxe mask launches in White.',
  },
  {
    id: 'voice',
    question: 'How Does The Voice Command Work?',
    answer: 'Short-press the white side button to activate voice mode, then say “Hi Mask.” The manual gives a 3-minute window for voice commands. Speak clearly in a quiet setting, and use the handheld controller as the primary control if a command is not recognized.',
  },
  {
    id: 'controller',
    question: 'Can I Use It Without Voice Control?',
    answer: 'Yes. The included handheld controller is the primary way to operate the mask. Voice control is a secondary option.',
  },
  {
    id: 'sessions',
    question: 'How Should I Use It?',
    answer:
      'Start with a clean, dry face and adjust the strap for comfort. The manual recommends a 5-minute test for first use and says not to exceed 20 minutes in a day. Read its safety instructions and follow the mode-specific schedule before use.',
  },
  {
    id: 'charging',
    question: 'How Is It Charged?',
    answer: 'Use the included USB-C cable. The manual lists a 3-hour charge time: red indicates charging and green indicates fully charged. Do not use the mask while it is charging.',
  },
  {
    id: 'cleaning',
    question: 'How Do I Clean The Mask?',
    answer: 'Wipe it with a damp microfiber cloth. Do not immerse it in water or use abrasive materials or strong cleaning agents. Follow the care instructions in the included manual.',
  },
  {
    id: 'who',
    question: 'Is It Designed For Men And Women?',
    answer: 'AuraGlow Luxe is offered for men and women. Follow the product manual for suitability and use.',
  },
  {
    id: 'orders',
    question: 'Where Can I Find Shipping And Order Details?',
    answer: 'Shipping is free. Orders usually ship in 1–2 days; this is the dispatch timeframe, not a delivery estimate. Tax is shown at checkout. For questions about your order, call our support team.',
  },
];

